// src/components/WeatherStation.js - Aggiornato con la stessa logica di Sensore5
import React, { useState, useEffect } from "react";
import { fetchWeatherStationData } from "../services/apiService";
import { formatItalianTime } from "../utils/timeUtils";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { format } from "date-fns";
import { it } from "date-fns/locale";

const WeatherStation = () => {
  const [weatherData, setWeatherData] = useState(null);
  const [historicalData, setHistoricalData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeMetric, setActiveMetric] = useState("temperatura"); // Metrica attiva per il grafico principale
  const [showDewPointGraph, setShowDewPointGraph] = useState(true); // Visibilità grafico punto di rugiada

  // Al caricamento del componente, recupera i dati della stazione meteo (Sensore5)
  useEffect(() => {
    const fetchWeatherData = async () => {
      try {
        setLoading(true);
        // Ottieni solo l'ultima lettura per i dati correnti
        const currentOptions = {
          limit: 1,
          aggregation: "none",
        };

        // Ottieni dati storici per il grafico (ultime 24 ore)
        const historicalOptions = {
          limit: 100,
          aggregation: "hourly",
          startDate:
            new Date(Date.now() - 24 * 60 * 60 * 1000)
              .toISOString()
              .split("T")[0] + " 00:00:00",
        };

        const current = await fetchWeatherStationData(currentOptions);
        const historical = await fetchWeatherStationData(historicalOptions);

        if (current && Array.isArray(current) && current.length > 0) {
          setWeatherData(current[0]);
        } else {
          setWeatherData(null);
        }

        if (historical && Array.isArray(historical) && historical.length > 0) {
          // Ordina i dati storici per data
          const sortedData = [...historical].sort((a, b) => {
            return new Date(a.dateString) - new Date(b.dateString);
          });
          setHistoricalData(sortedData);
        } else {
          setHistoricalData([]);
        }

        setLoading(false);
      } catch (err) {
        console.error("Errore nel caricamento dei dati meteo:", err);
        setError(err.message || "Errore nel caricamento dei dati");
        setLoading(false);
      }
    };

    fetchWeatherData();

    // Aggiorna i dati ogni 5 minuti
    const intervalId = setInterval(fetchWeatherData, 5 * 60 * 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  // Helper per la direzione del vento
  const getWindDirectionText = (degrees) => {
    if (degrees === null || degrees === undefined) return "N/A";

    const directions = [
      "N",
      "NNE",
      "NE",
      "ENE",
      "E",
      "ESE",
      "SE",
      "SSE",
      "S",
      "SSW",
      "SW",
      "WSW",
      "W",
      "WNW",
      "NW",
      "NNW",
    ];
    const index = Math.round(degrees / 22.5) % 16;
    return directions[index];
  };

  // Helper per verificare se un valore è un numero valido
  const isValidNumber = (value) => {
    return value !== null && value !== undefined && !isNaN(parseFloat(value));
  };

  // Helper per formattare numeri in modo sicuro
  const safeToFixed = (value, decimals = 1) => {
    if (!isValidNumber(value)) return "N/A";
    return parseFloat(value).toFixed(decimals);
  };

  // Configurazione delle metriche
  const metrics = [
    { id: "temperatura", label: "Temperatura", color: "#dc3545", unit: "°C" },
    { id: "umidita", label: "Umidità", color: "#0d6efd", unit: "%" },
    { id: "pressure", label: "Pressione", color: "#6c757d", unit: "hPa" },
    {
      id: "windSpeed",
      label: "Velocità vento",
      color: "#20c997",
      unit: "km/h",
    },
    {
      id: "lightIntensity",
      label: "Intensità luce",
      color: "#fd7e14",
      unit: "lux",
    },
    { id: "uvIndex", label: "Indice UV", color: "#fd7e14", unit: "" },
    { id: "dewPoint", label: "Punto di Rugiada", color: "#6610f2", unit: "°C" },
  ];

  // Trova la metrica attiva
  const currentMetric =
    metrics.find((m) => m.id === activeMetric) || metrics[0];

  // Calcola dinamicamente il dominio Y in base alla metrica attiva
  const getChartDomain = (dataKey) => {
    if (!historicalData || !historicalData.length) {
      const defaults = {
        temperatura: [0, 40],
        umidita: [0, 100],
        pressure: [900, 1100],
        windSpeed: [0, 100],
        lightIntensity: [0, 1000],
        uvIndex: [0, 10],
        dewPoint: [0, 40],
      };
      return defaults[dataKey] || [0, 100];
    }

    const values = historicalData
      .map((d) => parseFloat(d[dataKey]))
      .filter((v) => !isNaN(v) && v !== null);

    if (!values.length) {
      return [0, 100];
    }

    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;
    const padding = range * 0.1;

    return [Math.floor(min - padding), Math.ceil(max + padding)];
  };

  // Se sta caricando, mostra un indicatore
  if (loading && !weatherData) {
    return (
      <div className="card shadow-sm">
        <div className="card-body text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Caricamento...</span>
          </div>
          <p className="mt-2">Caricamento dati meteo...</p>
        </div>
      </div>
    );
  }

  // Se c'è un errore, mostralo
  if (error && !weatherData) {
    return (
      <div className="card shadow-sm border-danger">
        <div className="card-body">
          <h3 className="fs-5 text-danger">Errore stazione meteo</h3>
          <p>{error}</p>
          <p className="text-muted small">
            La stazione meteo potrebbe non essere disponibile. Ricarica la
            pagina per riprovare.
          </p>
        </div>
      </div>
    );
  }

  // Se non ci sono dati, mostra un messaggio
  if (!weatherData) {
    return (
      <div className="card shadow-sm">
        <div className="card-body">
          <h3 className="fs-5">Stazione Meteo</h3>
          <p className="text-muted">Nessun dato meteo disponibile.</p>
        </div>
      </div>
    );
  }

  // Altrimenti, mostra i dati meteo
  return (
    <div className="card shadow-sm">
      <div className="card-header py-2 px-3 bg-primary bg-opacity-10">
        <div className="d-flex justify-content-between align-items-center">
          <h3 className="fs-5 fw-bold mb-0">Stazione Meteo</h3>
          <span className="badge bg-secondary text-white small">
            {weatherData.dateString
              ? formatItalianTime(weatherData.dateString, "dd/MM HH:mm")
              : "N/A"}
          </span>
        </div>
      </div>

      <div className="card-body p-3">
        <div className="row g-3">
          {/* Temperatura e Umidità */}
          <div className="col-md-4">
            <div className="d-flex justify-content-between">
              <div className="me-4">
                <div className="text-danger fs-1 fw-bold">
                  {safeToFixed(weatherData.temperatura)}°C
                </div>
                <div className="text-muted small">Temperatura</div>
              </div>
              <div>
                <div className="text-primary fs-1 fw-bold">
                  {safeToFixed(weatherData.umidita, 0)}%
                </div>
                <div className="text-muted small">Umidità</div>
              </div>
            </div>
          </div>

          {/* Pressione e UV */}
          <div className="col-md-4">
            <div className="d-flex justify-content-between">
              <div>
                <div className="me-4">
                  <div className="text-secondary fs-3 fw-bold">
                    {isValidNumber(weatherData.pressure)
                      ? safeToFixed(weatherData.pressure, 0)
                      : "N/A"}{" "}
                    hPa
                  </div>
                  <div className="text-muted small">Pressione</div>
                </div>
              </div>
              <div>
                <div className="text-warning fs-3 fw-bold">
                  {isValidNumber(weatherData.uvIndex)
                    ? safeToFixed(weatherData.uvIndex)
                    : isValidNumber(weatherData.uv_index)
                      ? safeToFixed(weatherData.uv_index)
                      : "N/A"}
                </div>
                <div className="text-muted small">Indice UV</div>
              </div>
            </div>
          </div>

          {/* Pioggia */}
          <div className="col-md-3">
            <div>
              <div className="text-info fs-3 fw-bold">
                {isValidNumber(weatherData.rainGauge)
                  ? `${safeToFixed(weatherData.rainGauge, 1)} mm`
                  : "0.0 mm"}
              </div>
              <div className="text-muted small">Pioggia</div>
            </div>
          </div>

          {/* Vento e Luminosità */}
          <div className="col-md-4">
            <div className="d-flex justify-content-between">
              <div>
                <div className="text-success fs-3 fw-bold">
                  {isValidNumber(weatherData.windSpeed)
                    ? safeToFixed(weatherData.windSpeed)
                    : isValidNumber(weatherData.wind_speed)
                      ? safeToFixed(weatherData.wind_speed)
                      : "N/A"}{" "}
                  km/h
                </div>
                <div className="text-muted small">
                  Vento da{" "}
                  {getWindDirectionText(
                    weatherData.windDirection !== undefined
                      ? weatherData.windDirection
                      : weatherData.wind_direction,
                  )}
                </div>
              </div>
              <div>
                <div className="text-warning fs-3 fw-bold">
                  {isValidNumber(weatherData.lightIntensity)
                    ? parseInt(weatherData.lightIntensity)
                    : isValidNumber(weatherData.light_intensity)
                      ? parseInt(weatherData.light_intensity)
                      : "N/A"}
                </div>
                <div className="text-muted small">Luminosità</div>
              </div>
            </div>
          </div>
          <div className="col-md-2">
            <div>
              <div
                className="text-primary fs-3 fw-bold"
                style={{ color: "#6610f2" }}
              >
                {isValidNumber(weatherData.dewPoint)
                  ? safeToFixed(weatherData.dewPoint)
                  : "N/A"}
                °C
              </div>
              <div className="text-muted small">Punto di Rugiada</div>
            </div>
          </div>
        </div>

        {/* Selettore della metrica per il grafico principale */}
        <div className="mt-4 mb-2">
          <div className="d-flex flex-wrap gap-1">
            {metrics.map((metric) => (
              <button
                key={metric.id}
                onClick={() => setActiveMetric(metric.id)}
                className={`btn btn-sm rounded-pill ${
                  activeMetric === metric.id
                    ? "btn-primary"
                    : "btn-outline-secondary"
                }`}
                style={{ fontSize: "0.75rem" }}
              >
                {metric.label}
              </button>
            ))}
          </div>
        </div>

       

        {/* Grafico principale (metrica selezionata) */}
        {historicalData.length > 0 && (
          <div className="mt-3" style={{ height: "180px" }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={historicalData}
                margin={{ top: 10, right: 10, left: 0, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="dateString"
                  tick={{ fontSize: 10 }}
                  interval="preserveStartEnd"
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return format(date, "HH:mm", { locale: it });
                  }}
                />
                <YAxis
                  domain={getChartDomain(activeMetric)}
                  tick={{ fontSize: 10 }}
                  width={35}
                  tickFormatter={(value) => {
                    if (activeMetric === "lightIntensity") {
                      return parseInt(value);
                    }
                    return parseFloat(value).toFixed(1);
                  }}
                />
                <Tooltip
                  labelFormatter={(value) =>
                    `Data: ${formatItalianTime(value, "dd/MM/yyyy HH:mm")}`
                  }
                  formatter={(value) => [
                    `${
                      isValidNumber(value)
                        ? activeMetric === "lightIntensity"
                          ? parseInt(value)
                          : parseFloat(value).toFixed(1)
                        : "N/A"
                    } ${currentMetric.unit}`,
                    currentMetric.label,
                  ]}
                  contentStyle={{ fontSize: "12px" }}
                />
                <Line
                  type="monotone"
                  dataKey={activeMetric}
                  stroke={currentMetric.color}
                  dot={false}
                  strokeWidth={2}
                  isAnimationActive={false}
                  connectNulls={true}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Grafico separato per il Punto di Rugiada */}
        {historicalData.length > 0 && (
          <div className="mt-4" style={{ height: "180px" }}>
            <h4 className="fw-semibold mb-2 text-muted small">
              Andamento Punto di Rugiada
            </h4>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={historicalData}
                margin={{ top: 10, right: 10, left: 0, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="dateString"
                  tick={{ fontSize: 10 }}
                  interval="preserveStartEnd"
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return format(date, "HH:mm", { locale: it });
                  }}
                />
                <YAxis
                  domain={getChartDomain("dewPoint")}
                  tick={{ fontSize: 10 }}
                  width={35}
                  tickFormatter={(value) => parseFloat(value).toFixed(1)}
                />
                <Tooltip
                  labelFormatter={(value) =>
                    `Data: ${formatItalianTime(value, "dd/MM/yyyy HH:mm")}`
                  }
                  formatter={(value) => [
                    isValidNumber(value)
                      ? `${parseFloat(value).toFixed(1)} °C`
                      : "N/A",
                    "Punto di Rugiada",
                  ]}
                  contentStyle={{ fontSize: "12px" }}
                />
                <Line
                  type="monotone"
                  dataKey="dewPoint"
                  stroke="#6610f2"
                  dot={false}
                  strokeWidth={2}
                  isAnimationActive={false}
                  connectNulls={true}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
      <div className="card-footer bg-transparent border-top-0 pb-3 pt-0">
        <div className="d-flex justify-content-end">
          <a
            href="/sensori/impostaallarmi.php"
            className="btn btn-outline-primary btn-sm"
          >
            <i className="bi bi-gear me-1"></i> Imposta Allarmi
          </a>
        </div>
      </div>
    </div>
  );
};

export default WeatherStation;
